import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Box,
  Button,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EmailIcon from '@mui/icons-material/Email';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory } from 'react-router-dom';
import UserTable from '../../components/company/UserTable';
import InviteUserModal from '../../components/company/InviteUserModal';
import UserFormModal from '../../components/company/UserFormModal';
import { fetchCompanyUsers, fetchCompanyStats } from '../../redux/slices/companySlice';
import { useHasPermission } from '../../hooks/usePermission';
import ForbiddenPage from '../ForbiddenPage';
import PermissionButton from '../../components/common/PermissionButton';

const CompanyUsersPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { users, usersTotal, usersPage, usersLimit, usersLoading } = useSelector(
    (state) => state.company
  );
  const { user: currentUser } = useSelector((state) => state.auth);
  
  // Permission-based guard (replaces userType guard)
  const hasPermission = useHasPermission('users.view');
  
  // Check if user is system admin (for company filter)
  const isSystemAdmin = !currentUser?.companyId;
  
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all'); // 'all' | companyId
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [userFormOpen, setUserFormOpen] = useState(false);
  
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Extract unique companies from users (for system admin filter dropdown)
  const companies = useMemo(() => {
    if (!isSystemAdmin || !users || users.length === 0) return [];
    
    const companyMap = new Map();
    users.forEach(user => {
      if (user.companyId && user.companyId._id) {
        const companyId = user.companyId._id;
        const companyName = user.companyId.companyName || 'Bilinmeyen Şirket';
        if (!companyMap.has(companyId)) {
          companyMap.set(companyId, {
            _id: companyId,
            name: companyName
          });
        }
      }
    });
    
    return Array.from(companyMap.values()).sort((a, b) => 
      a.name.localeCompare(b.name, 'tr')
    );
  }, [users, isSystemAdmin]);
  
  // Fetch users on mount & filter change
  useEffect(() => {
    if (hasPermission) {
      const params = {
        search: debouncedSearch,
        userType: userTypeFilter,
        status: statusFilter,
        page: usersPage,
        limit: usersLimit
      };
      
      // System admin can filter by companyId
      if (isSystemAdmin && companyFilter && companyFilter !== 'all') {
        params.companyId = companyFilter;
      }
      
      dispatch(fetchCompanyUsers(params));
      
      // Fetch stats with company filter
      const statsCompanyId = isSystemAdmin && companyFilter !== 'all' ? companyFilter : null;
      dispatch(fetchCompanyStats(statsCompanyId));
    }
  }, [dispatch, debouncedSearch, userTypeFilter, statusFilter, companyFilter, usersPage, usersLimit, isSystemAdmin, hasPermission]);
  
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handleInviteClick = () => {
    setInviteModalOpen(true);
  };

  const handleModalClose = () => {
    setInviteModalOpen(false);
    // Refresh users list after modal closes
    const params = {
      search: debouncedSearch,
      userType: userTypeFilter,
      status: statusFilter,
      page: usersPage,
      limit: usersLimit
    };
    
    if (isSystemAdmin && companyFilter && companyFilter !== 'all') {
      params.companyId = companyFilter;
    }
    
    dispatch(fetchCompanyUsers(params));
    
    const statsCompanyId = isSystemAdmin && companyFilter !== 'all' ? companyFilter : null;
    dispatch(fetchCompanyStats(statsCompanyId));
  };

  const handleViewInvites = () => {
    history.push('/invites');
  };
  
  // Permission-based guard (replaces userType guard)
  if (!hasPermission) {
    return <ForbiddenPage />;
  }
  
  return (
      <Container maxWidth="xl">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
          sx={{ mb: 3 }}
        >
          Geri Dön
        </Button>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {isSystemAdmin ? 'Tüm Kullanıcılar' : 'Şirket Kullanıcıları'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isSystemAdmin 
                ? 'Sistemdeki tüm kullanıcıları görüntüleyin ve yönetin' 
                : 'Şirketinize bağlı tüm kullanıcıları yönetin'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <PermissionButton
              permission="invites.view"
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={handleViewInvites}
            >
              Davetler
            </PermissionButton>
            <PermissionButton
              permission="users.create"
              variant="outlined"
              onClick={() => setUserFormOpen(true)}
            >
              Kullanıcı Oluştur
            </PermissionButton>
            <PermissionButton
              permission="invites.send"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleInviteClick}
            >
              Kullanıcı Davet Et
            </PermissionButton>
          </Box>
        </Box>
        
        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Search */}
            <TextField
              placeholder="Ad, soyad veya email ile ara..."
              value={searchTerm}
              onChange={handleSearch}
              sx={{ flex: 1, minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            
            {/* Company Filter (System Admin only) */}
            {isSystemAdmin && (
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Şirket</InputLabel>
                <Select
                  value={companyFilter}
                  label="Şirket"
                  onChange={(e) => setCompanyFilter(e.target.value)}
                >
                  <MenuItem value="all">Tüm Şirketler</MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company._id} value={company._id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            {/* User Type Filter */}
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Kullanıcı Tipi</InputLabel>
              <Select
                value={userTypeFilter}
                label="Kullanıcı Tipi"
                onChange={(e) => setUserTypeFilter(e.target.value)}
              >
                <MenuItem value="">Tümü</MenuItem>
                <MenuItem value="company_owner">Şirket Sahibi</MenuItem>
                <MenuItem value="company_member">Şirket Üyesi</MenuItem>
                {isSystemAdmin && (
                  <>
                    <MenuItem value="system_admin">Sistem Yöneticisi</MenuItem>
                    <MenuItem value="individual">Bireysel</MenuItem>
                  </>
                )}
              </Select>
            </FormControl>
            
            {/* Status Filter */}
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Durum</InputLabel>
              <Select
                value={statusFilter}
                label="Durum"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">Tümü</MenuItem>
                <MenuItem value="active">Aktif</MenuItem>
                <MenuItem value="inactive">Pasif</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>
        
        {/* User Table */}
        <UserTable
          users={users}
          total={usersTotal}
          page={usersPage}
          limit={usersLimit}
          loading={usersLoading}
        />

        {/* Invite User Modal */}
        <InviteUserModal 
          open={inviteModalOpen} 
          onClose={handleModalClose}
        />
        <UserFormModal
          open={userFormOpen}
          onClose={() => setUserFormOpen(false)}
          mode="create"
          onSuccess={() => {
            const params = {
              search: debouncedSearch,
              userType: userTypeFilter,
              status: statusFilter,
              page: usersPage,
              limit: usersLimit
            };
            if (isSystemAdmin && companyFilter && companyFilter !== 'all') {
              params.companyId = companyFilter;
            }
            dispatch(fetchCompanyUsers(params));
            const statsCompanyId = isSystemAdmin && companyFilter !== 'all' ? companyFilter : null;
            dispatch(fetchCompanyStats(statsCompanyId));
          }}
        />
      </Container>
  );
};

export default CompanyUsersPage;

